<template>
    <div class="event-detail-view">
        <div class="event-detail-view__card">
            <div class="event-detail-view__info">
                <span class="event-detail-view__chip">{{ langJson('Upcoming') }}</span>
                <h1 class="event-detail-view__title">{{ event.title }}</h1>
                <p class="event-detail-view__host" v-if="event.organizerName">
                    {{ langJson('hosted by') }} <strong>{{ event.organizerName }}</strong>
                </p>
                <p class="event-detail-view__links" v-if="event.termsUrl || event.websiteUrl">
                    <a v-if="event.termsUrl" :href="event.termsUrl" target="_blank" rel="noopener">{{ langJson('Terms & Conditions') }}</a>
                    <span v-if="event.termsUrl && event.websiteUrl" class="event-detail-view__sep">|</span>
                    <a v-if="event.websiteUrl" :href="event.websiteUrl" target="_blank" rel="noopener" class="event-detail-view__site">{{ websiteHost }}</a>
                </p>
                <div class="event-detail-view__block">
                    <p class="event-detail-view__label">{{ langJson('Venue') }}</p>
                    <p class="event-detail-view__value">{{ event.venue || langJson('Venue to be announced') }}</p>
                </div>
                <div class="event-detail-view__block">
                    <p class="event-detail-view__label">{{ langJson('Date(s)') }}</p>
                    <p class="event-detail-view__value">{{ dates }}</p>
                </div>
                <div class="event-detail-view__block">
                    <p class="event-detail-view__label">{{ langJson('Description') }}</p>
                    <p class="event-detail-view__value event-detail-view__value--desc">{{ event.description }}</p>
                    <img v-if="event.descriptionImageUrl" :src="event.descriptionImageUrl" class="event-detail-view__desc-img" alt="" />
                </div>
            </div>
            <div class="event-detail-view__banner" :style="event.ticketImageUrl ? { backgroundImage: `url(${event.ticketImageUrl})` } : {}"></div>
        </div>

        <div class="event-detail-view__tickets">
            <div class="event-detail-view__tickets-head">
                <h2>{{ langJson('Tickets') }}</h2>
                <div class="event-detail-view__actions">
                    <button v-if="event.seatmapImageUrl" class="event-detail-view__btn event-detail-view__btn--outline" disabled>{{ langJson('View seatmap') }}</button>
                    <button class="event-detail-view__btn event-detail-view__btn--gradient" disabled>{{ langJson('Get tickets') }}</button>
                </div>
            </div>
            <div v-if="tiers.length" class="event-detail-view__tiers">
                <div v-for="tier in tiers" :key="tier.id" class="event-detail-view__tier">
                    <h3>{{ tier.name }}</h3>
                    <ul v-if="tier.description">
                        <li v-for="(line, i) in bullets(tier.description)" :key="i">{{ line }}</li>
                    </ul>
                    <div class="event-detail-view__tier-foot">
                        <span>{{ price(tier) }}</span>
                        <button class="event-detail-view__btn event-detail-view__btn--sm" disabled>{{ langJson('Select') }}</button>
                    </div>
                </div>
            </div>
            <p v-else class="event-detail-view__empty">{{ langJson('Ticket categories will appear here once they are set up.') }}</p>
        </div>
    </div>
</template>
<script>
import { formatEventDates } from '@/assets/js/organizerEvent'

/**
 * Read-only render of the public event page from organiser data, for Preview.
 * Buttons are inert — nothing here can start a checkout.
 */
export default {
    props: {
        event: { type: Object, required: true },
    },
    computed: {
        dates() {
            return formatEventDates(this.event.eventDate, this.event.eventEndDate, this.event.timezone)
        },
        websiteHost() {
            try {
                return new URL(this.event.websiteUrl).host.replace(/^www\./, '')
            } catch (e) {
                return this.event.websiteUrl
            }
        },
        tiers() {
            return this.event.tiers || []
        },
    },
    methods: {
        bullets(text) {
            return text
                .split('\n')
                .map((line) => line.replace(/^[\s•\-*]+/, '').trim())
                .filter(Boolean)
        },
        price(tier) {
            if (tier.price == null) return ''
            const currency = (tier.currency || this.event.currency || 'USD').toUpperCase()
            try {
                return new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 2 }).format(tier.price)
            } catch (e) {
                return `${currency} ${tier.price}`
            }
        },
    },
}
</script>
<style lang="scss" scoped>
$gradient: linear-gradient(90deg, #e879b2 0%, #a855f7 100%);

.event-detail-view {
    display: flex;
    flex-direction: column;
    gap: 40px;
    font-family: 'Inter', sans-serif;
    color: var(--font_deep_color);

    p,
    h1,
    h2,
    h3 {
        margin: 0;
    }

    &__card {
        background: var(--color_white);
        border-radius: 24px;
        padding: 32px;
        display: flex;
        gap: 40px;
    }
    &__info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    &__chip {
        align-self: flex-start;
        padding: 4px 12px;
        border-radius: 50px;
        background: #f1e9ff;
        color: #8b5cf6;
        font-size: 12px;
    }
    &__title {
        font-family: 'Cabinet Grotesk';
        font-size: 32px;
        font-weight: 800;
        line-height: 1.2;
    }
    &__host {
        font-size: 14px;
        color: var(--font_color);
    }
    &__links {
        font-size: 13px;
        display: flex;
        gap: 8px;

        a {
            color: var(--main_color);
            text-decoration: none;
        }
    }
    &__site {
        color: var(--font_color) !important;
    }
    &__sep {
        color: var(--border_color);
    }
    &__block {
        margin-top: 12px;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    &__label {
        font-size: 14px;
        color: var(--font_modif_color);
    }
    &__value {
        font-size: 15px;
        font-weight: 700;

        &--desc {
            font-weight: 700;
            line-height: 1.6;
            white-space: pre-line;
        }
    }
    &__desc-img {
        margin-top: 12px;
        max-width: 100%;
        border-radius: 16px;
    }
    &__banner {
        width: 46%;
        flex-shrink: 0;
        aspect-ratio: 1 / 1;
        border-radius: 24px;
        background: #e4e7ec center / cover no-repeat;
    }
    &__tickets {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
    &__tickets-head {
        display: flex;
        justify-content: space-between;
        align-items: center;

        h2 {
            font-size: 24px;
            font-weight: 800;
        }
    }
    &__actions {
        display: flex;
        gap: 12px;
    }
    &__btn {
        border-radius: 50px;
        padding: 12px 24px;
        font-family: inherit;
        font-size: 14px;
        font-weight: 700;
        cursor: default;
        white-space: nowrap;

        &--gradient {
            background: $gradient;
            border: none;
            color: #fff;
        }
        &--outline {
            background: var(--color_white);
            border: 1.5px solid #a855f7;
            color: #a855f7;
        }
        &--sm {
            padding: 6px 16px;
            font-size: 13px;
            background: var(--color_white);
            border: 1.5px solid #a855f7;
            color: #a855f7;
        }
    }
    &__tiers {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 24px;
    }
    &__tier {
        background: var(--color_white);
        border-radius: 20px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;

        h3 {
            font-size: 18px;
            font-weight: 800;
        }
        ul {
            margin: 0;
            padding-left: 18px;
            font-size: 13px;
            color: var(--font_color);
            line-height: 1.6;
            flex: 1;
        }
    }
    &__tier-foot {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
        color: var(--font_color);
    }
    &__empty {
        font-family: 'Lato';
        color: var(--font_modif_color);
    }
}

@media only screen and (max-width: 1080px) {
    .event-detail-view {
        gap: 24px;

        &__card {
            flex-direction: column-reverse;
            padding: 16px;
            gap: 16px;
        }
        &__banner {
            width: 100%;
        }
        &__tickets-head {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
        }
    }
}
</style>
